<script lang="ts" setup>
import {cn} from '@/lib/utils';
import {CalendarRoot, type CalendarRootEmits, type CalendarRootProps, useForwardPropsEmits} from 'reka-ui';
import {type HTMLAttributes} from 'vue';
import {
    CalendarGrid,
    CalendarGridBody,
    CalendarGridHead,
    CalendarGridRow,
    CalendarHeadCell,
    CalendarHeader,
    CalendarHeading,
    CalendarNextButton,
    CalendarPrevButton,
    CalendarCell,
    CalendarCellTrigger
} from '@/core/components/ui/calendar';
import {DateValue} from "@internationalized/date";

const {class: componentClass, ...delegatedProps} = defineProps<CalendarRootProps & {
    class?: HTMLAttributes['class'];
}>();

const emits = defineEmits<CalendarRootEmits>();
const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
  <CalendarRoot
    v-slot="{ grid, weekDays }"
    :class="cn('p-3', componentClass)"
    v-bind="forwarded"
    locale="fr-FR"
    multiple
  >
    <CalendarHeader>
      <CalendarPrevButton />
      <CalendarHeading />
      <CalendarNextButton />
    </CalendarHeader>

    <div class="flex flex-col gap-y-4 mt-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
      <CalendarGrid v-for="month in grid" :key="month.value.toString()">
        <CalendarGridHead>
          <CalendarGridRow>
            <CalendarHeadCell
              v-for="day in weekDays"
              :key="day" class="flex-1"
            >
              {{ day }}
            </CalendarHeadCell>
          </CalendarGridRow>
        </CalendarGridHead>
        <CalendarGridBody>
          <CalendarGridRow v-for="(weekDates, index) in month.rows" :key="`weekDate-${index}`" class="w-full">
            <CalendarCell
              v-for="date in weekDates as DateValue[]"
              :key="date.toString()"
              :date="date"
              class="relative h-auto p-0 flex-1 [&:has([data-selected][data-outside-view])]:bg-primary/30"
            >
              <div class="w-full h-full flex flex-col items-center">
                <CalendarCellTrigger
                  :day="date"
                  :month="month.value"
                  :class="cn(
                    'relative data-[selected]:bg-primary data-[selected]:hover:bg-primary/80',
                  )"
                >
                  {{ date.day }}
                </CalendarCellTrigger>
              </div>
            </CalendarCell>
          </CalendarGridRow>
        </CalendarGridBody>
      </CalendarGrid>
    </div>
  </CalendarRoot>
</template>
